/**
 * Item detail — V1.0.x catalogue detail surface (ADR-0013 redesign).
 *
 * Mirrors `prototype/catalogue/item-detail.html` verbatim:
 *   • Sticky composer-head with breadcrumb + actions.
 *   • 4-cell cover-grid (item identity · stock · pricing · vendor).
 *   • Tabs (Overview · Pricing history · Where sourced · Activity).
 *   • Overview body has a description panel, specs panel, tags chip
 *     card, and quick-facts rail.
 *
 * Class names are kept verbatim from the prototype. Page-scoped CSS
 * lives in `item-detail-page.css`.
 *
 * @see prototype/catalogue/item-detail.html
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Copy,
  Download,
  Edit3,
  Hammer,
  Package,
  Plus,
  Printer,
  Shirt,
} from "lucide-react";
import type { items as itemsContract } from "@flint/api-contract";
import type { z } from "zod";
import { avatarClassFor, initialsFor } from "../../master-list/index.ts";
import { trpc } from "../../../lib/trpc.ts";
import { usePermission } from "../../../lib/permissions.ts";
import { meQueryOptions } from "../../../lib/session.ts";
import { FabricSpecsPanel } from "./fabric-specs-panel.tsx";
import { FabricInspectionsSection } from "./fabric-inspections-section.tsx";
import { downloadItemXlsx } from "./detail-export.ts";
import "./item-detail-page.css";

type ItemRow = z.infer<typeof itemsContract.itemRow>;
type TabId = "overview" | "pricing" | "sourced" | "activity" | "fabric";

export interface ItemDetailPageProps {
  itemId: string;
  /** Deep-link the opening tab (e.g. `?tab=fabric` from the inward board). */
  initialTab?: string | undefined;
}

const TAB_IDS: readonly TabId[] = ["overview", "pricing", "sourced", "activity", "fabric"];
const isTabId = (v: string | undefined): v is TabId => v !== undefined && (TAB_IDS as readonly string[]).includes(v);

export function ItemDetailPage({ itemId, initialTab }: ItemDetailPageProps) {
  const [tab, setTab] = useState<TabId>(isTabId(initialTab) ? initialTab : "overview");
  const query = trpc.items.get.useQuery({ id: itemId });
  const activity = trpc.items.activity.useQuery(
    { id: itemId, limit: 20 },
    { enabled: tab === "pricing" || tab === "activity" },
  );
  const me = useQuery(meQueryOptions);
  const garmentMaterials = me.data?.company?.modules?.includes("garment.materials") ?? false;
  const canManageItems = usePermission("items.manage");

  if (query.isLoading) {
    return (
      <div className="item-detail-page main">
        <p className="text-muted">Loading…</p>
      </div>
    );
  }
  if (query.error) {
    return (
      <div className="item-detail-page main">
        <p className="text-bad">Couldn't load item: {query.error.message}</p>
      </div>
    );
  }
  if (!query.data) return null;
  const item = query.data as ItemRow;

  const ratePaise = item.defaultRatePaise ? BigInt(item.defaultRatePaise) : null;
  const costPaise = item.lastCostPaise ? BigInt(item.lastCostPaise) : null;
  const margin =
    ratePaise && costPaise && ratePaise !== 0n
      ? Math.round(Number((ratePaise - costPaise) * 100n) / Number(ratePaise))
      : null;
  const marginCls = margin === null ? "na" : margin >= 20 ? "" : margin >= 10 ? "warn" : "bad";

  return (
    <div className="item-detail-page">
      <main className="main">
        {/* ── Cover card — mirrors customer-detail-cover pattern ─────── */}
        <section className="item-cover" aria-label="Item profile">
          {/* Top: back · avatar · name/tagline/pills · actions */}
          <div className="item-cover-top">
            <Link to="/sales/items" className="item-cover-back" aria-label="Back to items">
              <ArrowLeft size={16} strokeWidth={2.2} aria-hidden />
            </Link>

            <span
              aria-hidden
              className={`item-cover-avatar ${avatarClassFor(item.name)}`}
            >
              {initialsFor(item.name)}
            </span>

            <div className="item-cover-head-body">
              <div className="item-cover-name-row">
                <h1 className="item-cover-name">{item.name}</h1>
              </div>
              <p className="item-cover-tagline">
                {[item.sku, item.unit, item.brand, item.hsn ? `HSN ${item.hsn}` : null]
                  .filter(Boolean)
                  .join(" · ") || "No SKU on file"}
              </p>
              <div className="item-cover-pills">
                <span className={`item-cover-pill ${item.itemType === "service" ? "violet" : "sky"} no-dot`}>
                  {item.itemType === "service" ? <Hammer size={9} aria-hidden /> : <Package size={9} aria-hidden />}
                  {capitalize(item.itemType)}
                </span>
                <span className={`item-cover-pill no-dot ${item.usage === "sale" ? "emerald" : item.usage === "purchase" ? "amber" : ""}`}>
                  {capitalize(item.usage)}
                </span>
                {item.category ? (
                  <span className="item-cover-pill indigo no-dot">{item.category}</span>
                ) : null}
                {item.tags.map((t) => (
                  <span key={t} className="item-cover-pill no-dot">{t}</span>
                ))}
              </div>
            </div>

            <div className="item-cover-actions">
              {canManageItems ? (
                <Link
                  to="/sales/items/new"
                  search={{ duplicateFrom: item.id }}
                  className="item-cover-btn secondary"
                >
                  <Copy size={13} aria-hidden /> Duplicate
                </Link>
              ) : (
                <button
                  type="button"
                  disabled
                  className="item-cover-btn secondary"
                  title="You need items.manage to duplicate an item."
                  style={{ opacity: 0.5, cursor: "not-allowed" }}
                >
                  <Copy size={13} aria-hidden /> Duplicate
                </button>
              )}
              <button
                type="button"
                onClick={() => void downloadItemXlsx(item)}
                className="item-cover-btn secondary"
              >
                <Download size={13} aria-hidden /> Export
              </button>
              <button
                type="button"
                onClick={() => window.print()}
                className="item-cover-btn secondary"
              >
                <Printer size={13} aria-hidden /> Print
              </button>
              {canManageItems ? (
                <Link
                  to="/sales/items/$itemId/edit"
                  params={{ itemId: item.id }}
                  className="item-cover-btn primary"
                >
                  <Edit3 size={13} aria-hidden /> Edit item
                </Link>
              ) : null}
            </div>
          </div>

          {/* Bottom: 3 info cells */}
          <div className="item-cover-grid">
            {/* Cell 1 — Identity */}
            <div className="item-cover-cell">
              <div className="item-cover-cell-head">
                <Package size={11} aria-hidden />
                Identity
              </div>
              <p className="item-cover-line">
                <span style={{ color: "var(--muted)", minWidth: 68 }}>SKU</span>
                <strong>{item.sku ?? "—"}</strong>
              </p>
              <p className="item-cover-line">
                <span style={{ color: "var(--muted)", minWidth: 68 }}>Type</span>
                <strong>{capitalize(item.itemType)}</strong>
              </p>
              <p className="item-cover-line">
                <span style={{ color: "var(--muted)", minWidth: 68 }}>Usage</span>
                <strong>{capitalize(item.usage)}</strong>
              </p>
              <p className="item-cover-line">
                <span style={{ color: "var(--muted)", minWidth: 68 }}>Category</span>
                <strong>{item.category ?? "—"}</strong>
              </p>
              <p className="item-cover-line">
                <span style={{ color: "var(--muted)", minWidth: 68 }}>Unit</span>
                <strong>{item.unit}</strong>
              </p>
            </div>

            {/* Cell 2 — Pricing */}
            <div className="item-cover-cell">
              <div className="item-cover-cell-head">
                Pricing
              </div>
              <div className="item-cover-price">{formatRupees(ratePaise) ?? "—"}</div>
              <p className="item-cover-line">
                <span style={{ color: "var(--muted)", minWidth: 68 }}>Last cost</span>
                <strong>{formatRupees(costPaise) ?? "—"}</strong>
              </p>
              <p className="item-cover-line">
                <span style={{ color: "var(--muted)", minWidth: 68 }}>GST</span>
                <strong>
                  {item.taxPreference === "taxable"
                    ? item.defaultTaxRateBps !== null
                      ? `${item.defaultTaxRateBps / 100}%`
                      : "—"
                    : labelPreference(item.taxPreference)}
                </strong>
              </p>
              {margin !== null ? (
                <span className={`margin-pill ${marginCls}`} style={{ marginTop: 6, display: "inline-flex" }}>
                  ↗ {margin}% margin
                </span>
              ) : null}
            </div>

            {/* Cell 3 — Stock & Vendor */}
            <div className="item-cover-cell">
              <div className="item-cover-cell-head">
                Stock &amp; Vendor
              </div>
              <p className="item-cover-line">
                <span style={{ color: "var(--muted)", minWidth: 68 }}>Tracking</span>
                <strong>{item.trackStock ? "on" : "off"}</strong>
              </p>
              <p className="item-cover-line">
                <span style={{ color: "var(--muted)", minWidth: 68 }}>Reorder</span>
                <strong>{item.reorderLevel ? `${item.reorderLevel} ${item.unit}` : "not set"}</strong>
              </p>
              {item.preferredVendorName ? (
                <>
                  <p className="item-cover-line" style={{ marginTop: 6 }}>
                    <span className="inline-block h-2 w-2 rounded-full flex-shrink-0" style={{ background: "var(--ok)", marginTop: 4 }} aria-hidden />
                    <strong>{item.preferredVendorName}</strong>
                  </p>
                  <p className="item-cover-line muted">
                    Lead {item.leadTimeDays ?? "—"} d · MOQ {item.minOrderQty ?? "—"}
                  </p>
                  <Link to="/procurement/vendors" className="item-cover-line" style={{ marginTop: 2 }}>
                    View vendor <ChevronRight size={11} aria-hidden />
                  </Link>
                </>
              ) : (
                <p className="item-cover-line muted" style={{ marginTop: 6 }}>
                  No preferred vendor — add one in the editor.
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Tabs */}
        <div className="tabs" role="tablist" aria-label="Item details">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "overview"}
            className={`tab ${tab === "overview" ? "is-active" : ""}`}
            onClick={() => setTab("overview")}
          >
            Overview
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "pricing"}
            className={`tab ${tab === "pricing" ? "is-active" : ""}`}
            onClick={() => setTab("pricing")}
          >
            Pricing history
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "sourced"}
            className={`tab ${tab === "sourced" ? "is-active" : ""}`}
            onClick={() => setTab("sourced")}
          >
            Where sourced
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "activity"}
            className={`tab ${tab === "activity" ? "is-active" : ""}`}
            onClick={() => setTab("activity")}
          >
            Activity
          </button>
          {garmentMaterials ? (
            <button
              type="button"
              role="tab"
              aria-selected={tab === "fabric"}
              className={`tab ${tab === "fabric" ? "is-active" : ""}`}
              onClick={() => setTab("fabric")}
            >
              <Shirt size={12} aria-hidden style={{ marginRight: 4, verticalAlign: "-2px" }} />
              Fabric specs
            </button>
          ) : null}
        </div>

        {/* Panel: Overview */}
        {tab === "overview" ? (
          <section className="tab-panel" role="tabpanel">
            <div className="body-grid">
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div className="panel">
                  <div className="panel-head">
                    <h3>About</h3>
                    <span className="meta">
                      {item.updatedAt
                        ? `Updated ${formatDate(item.updatedAt)}`
                        : `Created ${formatDate(item.createdAt)}`}
                    </span>
                  </div>
                  <div className="panel-body">
                    {item.description ? (
                      <p className="desc">{item.description}</p>
                    ) : (
                      <p className="desc" style={{ color: "var(--muted)", fontStyle: "italic" }}>
                        No description yet. Operators can add one from the editor — it prints on the
                        line description by default.
                      </p>
                    )}
                  </div>
                </div>

                <div className="panel">
                  <div className="panel-head">
                    <h3>Specifications</h3>
                  </div>
                  <div className="panel-body">
                    <dl className="specs">
                      <SpecRow label="SKU" value={item.sku ?? "—"} mono />
                      <SpecRow label="HSN/SAC" value={item.hsn ?? "—"} mono />
                      <SpecRow label="Item type" value={capitalize(item.itemType)} />
                      <SpecRow label="Tax preference" value={labelPreference(item.taxPreference)} />
                      <SpecRow label="Category" value={item.category ?? "—"} />
                      <SpecRow label="Brand" value={item.brand ?? "—"} />
                      <SpecRow label="Manufacturer" value={item.manufacturer ?? "—"} />
                      <SpecRow label="Barcode" value={item.barcode ?? "—"} mono />
                      <SpecRow label="Default unit" value={item.unit} />
                      <SpecRow
                        label="Alt units"
                        value={
                          item.altUnits && item.altUnits.length > 0
                            ? item.altUnits.map((a) => `${a.unit} × ${a.factor}`).join(", ")
                            : "none configured"
                        }
                      />
                      <SpecRow
                        label="Lead time"
                        value={item.leadTimeDays !== null ? `${item.leadTimeDays} d` : "—"}
                        mono
                      />
                      <SpecRow
                        label="MOQ"
                        value={item.minOrderQty !== null ? `${item.minOrderQty} ${item.unit}` : "—"}
                      />
                    </dl>
                  </div>
                </div>
              </div>

              <aside className="rail">
                <div className="panel tags-card">
                  <div className="panel-head">
                    <h3>Tags</h3>
                    <span className="meta">{item.tags.length}</span>
                  </div>
                  <div className="panel-body">
                    {item.tags.map((t) => (
                      <span key={t} className="tag-chip">
                        {t}
                      </span>
                    ))}
                    <button type="button" className="tag-add">
                      <Plus size={10} aria-hidden /> tag
                    </button>
                  </div>
                </div>

                <div className="panel">
                  <div className="panel-head">
                    <h3>Quick facts</h3>
                  </div>
                  <div className="panel-body">
                    <dl className="specs" style={{ gridTemplateColumns: "1fr" }}>
                      <SpecRow label="Created" value={formatDate(item.createdAt)} />
                      {item.updatedAt ? (
                        <SpecRow label="Last updated" value={formatDate(item.updatedAt)} />
                      ) : null}
                      <SpecRow
                        label="MRP"
                        value={item.mrpPaise ? (formatRupees(BigInt(item.mrpPaise)) ?? "—") : "—"}
                      />
                      {item.cessRateBps !== null ? (
                        <SpecRow label="Cess" value={`${item.cessRateBps / 100}%`} />
                      ) : null}
                      {item.tdsSectionDefault ? (
                        <SpecRow label="TDS section" value={item.tdsSectionDefault} />
                      ) : null}
                    </dl>
                  </div>
                </div>
              </aside>
            </div>
          </section>
        ) : null}

        {tab === "pricing" ? (
          <section className="tab-panel" role="tabpanel">
            <div className="panel">
              <div className="panel-head">
                <h3>Pricing &amp; cost movements</h3>
                <span className="meta">
                  {activity.isLoading
                    ? "loading history…"
                    : `${countPricingRows(activity.data?.items ?? []) + 1} entries`}
                </span>
              </div>
              <div className="panel-body" style={{ padding: 0 }}>
                <div className="history-list">
                  {pricingRowsFromAudit(activity.data?.items ?? []).map((row) => (
                    <div key={row.id} className="history-row">
                      <span className="date">{formatDate(row.at)}</span>
                      <span className="what">{row.what}</span>
                      <span className="delta">{row.delta}</span>
                    </div>
                  ))}
                  <div className="history-row">
                    <span className="date">{formatDate(item.createdAt)}</span>
                    <span className="what">
                      Item created · default rate {formatRupees(ratePaise) ?? "not set"}, GST{" "}
                      {item.defaultTaxRateBps !== null
                        ? `${item.defaultTaxRateBps / 100}%`
                        : "not set"}
                      .
                    </span>
                    <span className="delta">—</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ) : null}

        {tab === "sourced" ? (
          <section className="tab-panel" role="tabpanel">
            <div className="panel">
              <div className="panel-head">
                <h3>Where sourced</h3>
                <span className="meta">
                  {item.preferredVendorName ? "1 preferred vendor" : "no vendor on file"}
                </span>
              </div>
              <div className="panel-body" style={{ padding: 0 }}>
                {item.preferredVendorName ? (
                  <div className="sourced-list">
                    <div className="sourced-row">
                      <span className="vendor">
                        <span className="v-dot" />
                        {item.preferredVendorName}
                        <span className="pref">Preferred</span>
                      </span>
                      <span className="cost">
                        {formatRupees(costPaise) ?? "—"} / {item.unit}
                      </span>
                      <span className="when">last cost on file</span>
                    </div>
                  </div>
                ) : (
                  <p className="desc" style={{ padding: 16, color: "var(--muted)" }}>
                    No vendor linked. Pick one in the editor to pre-fill the bill composer.
                  </p>
                )}
              </div>
            </div>
          </section>
        ) : null}

        {tab === "activity" ? (
          <section className="tab-panel" role="tabpanel">
            <div className="panel">
              <div className="panel-head">
                <h3>Activity</h3>
                <span className="meta">
                  {activity.isLoading
                    ? "loading…"
                    : `${(activity.data?.items.length ?? 0) + 1} events`}
                </span>
              </div>
              <div className="timeline">
                {(activity.data?.items ?? []).map((row) => (
                  <div key={row.id} className="timeline-row">
                    <span className="marker" aria-hidden>
                      {row.verb === "created" ? <Check size={8} /> : <Edit3 size={8} />}
                    </span>
                    <span className="connector" aria-hidden />
                    <div className="content">
                      <div className="date">{formatDate(row.at)}</div>
                      <div className="what">{describeAuditRow(row)}</div>
                      <div className="by">
                        {row.actorName ?? actorLabel(row.actorType)}
                        {row.actorRole ? ` · ${row.actorRole}` : ""}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="timeline-row">
                  <span className="marker" aria-hidden>
                    <Check size={8} />
                  </span>
                  <span className="connector" aria-hidden />
                  <div className="content">
                    <div className="date">{formatDate(item.createdAt)}</div>
                    <div className="what">Item created.</div>
                    <div className="by">via /items composer</div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ) : null}

        {tab === "fabric" && garmentMaterials ? (
          <section className="tab-panel" role="tabpanel">
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <FabricSpecsPanel itemId={item.id} />
              <FabricInspectionsSection itemId={item.id} />
            </div>
          </section>
        ) : null}
      </main>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────

function SpecRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{mono ? <span className="mono">{value}</span> : value}</dd>
    </div>
  );
}

function formatRupees(paise: bigint | null): string | null {
  if (paise === null) return null;
  const rupees = Number(paise) / 100;
  return `₹${rupees.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
}

function capitalize(s: string): string {
  return s[0]!.toUpperCase() + s.slice(1);
}

function labelPreference(p: ItemRow["taxPreference"]): string {
  switch (p) {
    case "taxable":
      return "Taxable";
    case "exempt":
      return "Exempt";
    case "nil_rated":
      return "Nil-rated";
    case "non_gst":
      return "Non-GST";
  }
}

// ── audit / activity mapping ──────────────────────────────────────

type AuditRow = z.infer<typeof itemsContract.activityRow>;

const PRICING_KEYS: readonly string[] = ["defaultRatePaise", "defaultTaxRateBps"];

function countPricingRows(rows: readonly AuditRow[]): number {
  return rows.filter((r) => r.verb === "updated" && rowTouchesPricing(r)).length;
}

function rowTouchesPricing(r: AuditRow): boolean {
  const changed = r.metadata["changedKeys"];
  if (!Array.isArray(changed)) return false;
  return changed.some((k) => typeof k === "string" && PRICING_KEYS.includes(k));
}

function pricingRowsFromAudit(rows: readonly AuditRow[]): Array<{
  id: string;
  at: string;
  what: string;
  delta: string;
}> {
  return rows
    .filter((r) => r.verb === "updated" && rowTouchesPricing(r))
    .map((r) => {
      const before = (r.metadata["before"] ?? {}) as Record<string, unknown>;
      const after = (r.metadata["after"] ?? {}) as Record<string, unknown>;
      const parts: string[] = [];
      let delta = "—";
      if ("defaultRatePaise" in after) {
        const b = paiseToLabel(before["defaultRatePaise"]);
        const a = paiseToLabel(after["defaultRatePaise"]);
        parts.push(`Rate ${b} → ${a}`);
        const bp = paiseValue(before["defaultRatePaise"]);
        const ap = paiseValue(after["defaultRatePaise"]);
        if (bp !== null && ap !== null && bp !== 0n) {
          const pct = Math.round(Number((ap - bp) * 100n) / Number(bp));
          delta = `${pct >= 0 ? "+" : ""}${pct}%`;
        }
      }
      if ("defaultTaxRateBps" in after) {
        const b = bpsToLabel(before["defaultTaxRateBps"]);
        const a = bpsToLabel(after["defaultTaxRateBps"]);
        parts.push(`GST ${b} → ${a}`);
      }
      return {
        id: r.id,
        at: r.at,
        what: parts.join(" · ") || "Pricing updated",
        delta,
      };
    });
}

function paiseValue(v: unknown): bigint | null {
  if (typeof v === "string" && /^\d+$/.test(v)) return BigInt(v);
  if (typeof v === "number" && Number.isInteger(v) && v >= 0) return BigInt(v);
  if (typeof v === "bigint") return v;
  return null;
}

function paiseToLabel(v: unknown): string {
  const p = paiseValue(v);
  return p === null ? "—" : (formatRupees(p) ?? "—");
}

function bpsToLabel(v: unknown): string {
  if (typeof v !== "number" || !Number.isFinite(v)) return "—";
  return `${v / 100}%`;
}

function describeAuditRow(r: AuditRow): string {
  if (r.verb === "created") return "Item created.";
  if (r.verb === "archived") return "Item archived.";
  if (r.verb === "updated") {
    const changed = r.metadata["changedKeys"];
    if (Array.isArray(changed) && changed.length > 0) {
      const labels = changed
        .filter((k): k is string => typeof k === "string")
        .map(friendlyFieldLabel);
      return `Updated ${labels.join(", ")}.`;
    }
    return "Item details updated.";
  }
  return `Item ${r.verb}.`;
}

function friendlyFieldLabel(key: string): string {
  switch (key) {
    case "defaultRatePaise": return "rate";
    case "defaultTaxRateBps": return "GST";
    case "lastCostPaise": return "cost";
    case "mrpPaise": return "MRP";
    case "reorderLevel": return "reorder level";
    case "preferredVendorId": return "preferred vendor";
    case "leadTimeDays": return "lead time";
    case "minOrderQty": return "MOQ";
    default:
      return key.replace(/([A-Z])/g, " $1").toLowerCase().trim();
  }
}

function actorLabel(t: AuditRow["actorType"]): string {
  switch (t) {
    case "web": return "web user";
    case "whatsapp": return "WhatsApp";
    case "api": return "API";
    case "system": return "system";
    case "ai": return "AI";
    case "buyer": return "storefront buyer";
  }
}
